import { i18next } from '@frontend/i18n';
import { TableColumnConfig } from '@frontend/design-system';
import { EligibilityReportTooltip } from '../components/common-components/eligibility-report-tooltip';
import { StaticAccordionId } from '../context/AccordionContext';
import { Benefit, Maximum } from '../types/insurance-report-api.types';
import {
  prepareDescriptionData,
  prepareLimitationData,
  prepareNotesData,
  prepareServiceDateColumn,
} from './table-helpers';

export const FAVORITE_CDT_CODES_ACCORDION_ID = 'favoriteCDTCodeTable';

export const STATIC_ACCORDION_IDS: StaticAccordionId[] = [
  'patientPayerDetails',
  'maximums',
  'deductibles',
  'provisions',
  'history',
  FAVORITE_CDT_CODES_ACCORDION_ID,
];

export const FREQUENCY_LIMITATION_COLUMNS_CONFIG: TableColumnConfig<Benefit>[] = [
  {
    id: 'code',
    Header: i18next.t('Code', { ns: 'insuranceEligibilityReport' }),
    accessor: 'code',
    cellRenderer: (value) => value || '',
    startingSortBy: 'asc',
    sortType: 'alphanumeric',
    maxWidth: 100,
  },
  {
    id: 'description',
    Header: i18next.t('Details', { ns: 'insuranceEligibilityReport' }),
    accessor: (data: Benefit) => {
      return prepareDescriptionData(data);
    },
    cellRenderer: (value) => ((value || []).length > 0 ? value.join(', \n') : ''),
    width: '125',
  },
  {
    id: 'coverage',
    Header: i18next.t('Coverage', { ns: 'insuranceEligibilityReport' }),
    accessor: 'coverage',
    cellRenderer: (value) => {
      if (value === null || value === undefined || value === '' || value === 0) return '-';
      if (!isNaN(value)) {
        return (value * 100).toFixed() + '%';
      }
      return '-';
    },
    maxWidth: 110,
  },
  {
    id: 'limitation',
    Header: i18next.t('Limitation', { ns: 'insuranceEligibilityReport' }),
    accessor: (data: Benefit) => {
      return prepareLimitationData(data);
    },
    cellRenderer: (value) => value || '',
    maxWidth: 120,
  },
  {
    id: 'limitationDetails',
    Header: i18next.t('Limitation Details', { ns: 'insuranceEligibilityReport' }),
    accessor: (data: Benefit) => {
      return prepareNotesData(data);
    },
    cellRenderer: (value) => ((value || []).length > 0 ? value.join(', \n') : ''),
    width: '125',
  },
  {
    id: 'lastServiceDate',
    Header: i18next.t('Last Service Date', { ns: 'insuranceEligibilityReport' }),
    accessor: 'serviceDates',
    cellRenderer: (value) => {
      const { title, hoverText } = prepareServiceDateColumn(value || []);
      return <EligibilityReportTooltip placement='left-start' title={title} hoverText={hoverText} />;
    },
    maxWidth: 180,
  },
];

export const PRINT_FREQUENCY_LIMITATION_COLUMNS_CONFIG: TableColumnConfig<Benefit>[] = [
  {
    id: 'code',
    Header: i18next.t('Code', { ns: 'insuranceEligibilityReport' }),
    accessor: 'code',
    cellRenderer: (value) => value || '',
    startingSortBy: 'asc',
    sortType: 'alphanumeric',
    width: '30',
  },
  {
    id: 'description',
    Header: i18next.t('Details', { ns: 'insuranceEligibilityReport' }),
    accessor: 'description',
    cellRenderer: (value) => value || '',
  },
  {
    id: 'coverage',
    Header: i18next.t('Coverage', { ns: 'insuranceEligibilityReport' }),
    accessor: 'coverage',
    cellRenderer: (value) => {
      const num = Number(value);
      if (value && !isNaN(num)) {
        return (num * 100).toFixed() + '%';
      }
      return '-';
    },
    width: '20',
  },
  {
    id: 'limitation',
    Header: i18next.t('Limitation', { ns: 'insuranceEligibilityReport' }),
    accessor: (data: Benefit) => {
      return prepareLimitationData(data);
    },
    cellRenderer: (value) => value || '',
    width: '30',
  },
  {
    id: 'limitationDetails',
    Header: i18next.t('Limitation Details', { ns: 'insuranceEligibilityReport' }),
    accessor: (data: Benefit) => {
      return prepareNotesData(data);
    },
    cellRenderer: (value) => ((value || []).length > 0 ? value.join('\n') : ''),
  },
  {
    id: 'serviceDate',
    Header: i18next.t('Service Date', { ns: 'insuranceEligibilityReport' }),
    accessor: 'serviceDates',
    cellRenderer: (value: string[]) => {
      return (value || []).length > 0 ? value.join('\n') : '';
    },
    width: '90',
  },
];

export const MAX_AND_DED_COLUMNS_CONFIG: TableColumnConfig<Maximum>[] = [
  {
    id: 'category',
    Header: i18next.t('Category', { ns: 'insuranceEligibilityReport' }),
    accessor: 'category',
    cellRenderer: (value) => value || '-',
    width: '80',
  },
  {
    id: 'remaining',
    Header: i18next.t('Remaining', { ns: 'insuranceEligibilityReport' }),
    accessor: 'remaining',
    cellRenderer: (value) => value || '-',
    width: '20',
  },
  {
    id: 'total',
    Header: i18next.t('Total', { ns: 'insuranceEligibilityReport' }),
    accessor: 'amount',
    cellRenderer: (value) => value || '-',
    width: '20',
  },
  {
    id: 'type',
    Header: i18next.t('Type', { ns: 'insuranceEligibilityReport' }),
    accessor: 'coverageLevel',
    cellRenderer: (value) => value || '-',
    width: '40',
  },
];

export const DEBOUNCE_TIME_IN_MILLISECS = 500;

export const constructCategoryCoverage = (minCoverage: number, maxCoverage: number): string => {
  if (!minCoverage && !maxCoverage) return '';

  const min = minCoverage ? Math.floor(minCoverage * 100) : null;
  const max = maxCoverage ? Math.floor(maxCoverage * 100) : null;

  if (!min) return `${max}%`;
  if (!max) return `${min}%`;
  if (min === max) return `${max}%`;

  return `${min} - ${max}%`;
};

export const ADD_CRED_BANNER_LOCAL_STORAGE_KEY = 'add-cred-banner-dismissed';

export const DEFAULT_ACCORDION_ID = 'default-accordion-id';
